import { useAdrCatalogueSearch, useEwcCatalogueSearch } from '@my/directory-api/hooks';
import { TextInput } from '@my/ui';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  adrFromCatalogue,
  ewcFromCatalogue,
  type AdrSelection,
  type EwcSelection,
} from './lib/productDraft';

// ────────────────────────────────────────────────────────────────────────────
// Pickers for the two official lists. Both search ON THE SERVER — ADR Table A
// is ~2,900 rows / 1.2 MB, far too much to hand the browser — so each is a
// text box with a result list under it rather than a select.
// ────────────────────────────────────────────────────────────────────────────

/** Wait for typing to pause before searching, so each keystroke is not a request. */
function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

/** Close a dropdown when the mouse goes down anywhere outside it. */
function useOutsideClose(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open, onClose]);
  return ref;
}

// ── ADR ─────────────────────────────────────────────────────────────────────

export interface AdrPickerProps {
  value: AdrSelection | null;
  onChange: (next: AdrSelection | null) => void;
  disabled?: boolean;
}

/**
 * Pick an ADR Table A row by UN number or name. Once picked it shows the row
 * — class, packing group, labels, HIN, tunnel code — so the operator can see
 * they have the right one of several (UN 1993 alone has six rows).
 */
export function AdrPicker({ value, onChange, disabled }: AdrPickerProps) {
  const { t } = useTranslation('configuration');
  const [searching, setSearching] = useState(value === null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebounced(query);
  const search = useAdrCatalogueSearch(debounced);
  const close = () => setOpen(false);
  const ref = useOutsideClose(open, close);

  // A selection arriving from outside (opening another product) shows it.
  useEffect(() => {
    setSearching(value === null);
  }, [value]);

  if (value && !searching) {
    return (
      <SelectionCard
        heading={
          <>
            <span className="rounded-sm bg-orange-100 px-1.5 py-0.5 font-mono text-[11px] font-bold text-orange-800">
              {t('products.adr.un', { un: value.un })}
            </span>
            <span className="font-semibold text-slate-900">{value.name}</span>
          </>
        }
        facts={[
          [t('products.adr.class'), value.class],
          [t('products.adr.packingGroup'), value.packingGroup],
          [t('products.adr.labels'), value.labels],
          [t('products.adr.hazardNumber'), value.hazardNumber],
          [t('products.adr.tunnelCode'), value.tunnelCode],
          [t('products.adr.transportCategory'), value.transportCategory],
        ]}
        footnote={value.edition}
        changeLabel={t('products.adr.change')}
        removeLabel={t('products.adr.remove')}
        onChange={() => {
          setSearching(true);
          setQuery('');
        }}
        onRemove={() => onChange(null)}
        disabled={disabled}
      />
    );
  }

  const entries = search.data?.entries ?? [];
  const showList = open && debounced.trim().length > 0;

  return (
    <div ref={ref} className="relative">
      <TextInput
        label={t('products.adr.searchLabel')}
        name="adrSearch"
        autoComplete="off"
        value={query}
        placeholder={t('products.adr.searchPlaceholder')}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            close();
            // Back to the row they had, if they were only changing it.
            if (value) setSearching(false);
          }
        }}
      />
      {showList && (
        <ResultList
          busy={search.isFetching}
          busyLabel={t('products.adr.searching')}
          empty={search.isSuccess && entries.length === 0}
          emptyLabel={t('products.adr.noResults', { q: debounced.trim() })}
          more={
            search.data && search.data.total > entries.length
              ? t('products.adr.more', { shown: entries.length, total: search.data.total })
              : null
          }
        >
          {entries.map((e) => (
            <ResultButton
              key={e.key}
              onPick={() => {
                onChange(adrFromCatalogue(e, search.data!.edition));
                setSearching(false);
                setQuery('');
                close();
              }}
            >
              <span className="w-14 shrink-0 font-mono text-[11px] font-bold text-orange-700">
                UN {e.un}
              </span>
              <span className="min-w-0 flex-1 text-slate-800">{e.name}</span>
              <span className="shrink-0 text-[11px] text-slate-500">
                {[e.class, e.packingGroup, e.classificationCode].filter(Boolean).join(' · ')}
              </span>
            </ResultButton>
          ))}
        </ResultList>
      )}
    </div>
  );
}

// ── EWC ─────────────────────────────────────────────────────────────────────

export interface EwcPickerProps {
  value: EwcSelection | null;
  onChange: (next: EwcSelection | null) => void;
  disabled?: boolean;
  /** The search box's label; the default reads "Waste code". */
  label?: string;
}

/** Pick a waste code from the EU List of Waste by code or description. */
export function EwcPicker({ value, onChange, disabled, label }: EwcPickerProps) {
  const { t } = useTranslation('configuration');
  const [searching, setSearching] = useState(value === null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebounced(query);
  const search = useEwcCatalogueSearch(debounced);
  const close = () => setOpen(false);
  const ref = useOutsideClose(open, close);

  useEffect(() => {
    setSearching(value === null);
  }, [value]);

  if (value && !searching) {
    return (
      <SelectionCard
        heading={
          <>
            <span className="rounded-sm bg-slate-200 px-1.5 py-0.5 font-mono text-[11px] font-bold text-slate-800">
              {value.code}
            </span>
            <span className="font-semibold text-slate-900">{value.description}</span>
            {value.hazardous && <HazardousBadge />}
          </>
        }
        changeLabel={t('products.ewc.change')}
        removeLabel={t('products.ewc.remove')}
        onChange={() => {
          setSearching(true);
          setQuery('');
        }}
        onRemove={() => onChange(null)}
        disabled={disabled}
      />
    );
  }

  const entries = search.data?.entries ?? [];
  const showList = open && debounced.trim().length > 0;

  return (
    <div ref={ref} className="relative">
      <TextInput
        label={label ?? t('products.ewc.searchLabel')}
        name="ewcSearch"
        autoComplete="off"
        value={query}
        placeholder={t('products.ewc.searchPlaceholder')}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            close();
            if (value) setSearching(false);
          }
        }}
      />
      {showList && (
        <ResultList
          busy={search.isFetching}
          busyLabel={t('products.ewc.searching')}
          empty={search.isSuccess && entries.length === 0}
          emptyLabel={t('products.ewc.noResults', { q: debounced.trim() })}
          more={
            search.data && search.data.total > entries.length
              ? t('products.ewc.more', { shown: entries.length, total: search.data.total })
              : null
          }
        >
          {entries.map((e) => (
            <ResultButton
              key={e.code}
              onPick={() => {
                onChange(ewcFromCatalogue(e));
                setSearching(false);
                setQuery('');
                close();
              }}
            >
              <span className="w-20 shrink-0 font-mono text-[11px] font-bold text-slate-700">
                {e.code}
              </span>
              <span className="min-w-0 flex-1 text-slate-800">{e.description}</span>
              {e.hazardous && <HazardousBadge />}
            </ResultButton>
          ))}
        </ResultList>
      )}
    </div>
  );
}

export interface EwcCodeListProps {
  value: EwcSelection[];
  /** The default code; null = the first. */
  defaultCode: string | null;
  onChange: (next: EwcSelection[], defaultCode: string | null) => void;
  disabled?: boolean;
}

/**
 * A product's waste codes: one card per code, with the default marked, and a
 * search box to add another. A product can have several because a waste code
 * says where the waste came from; each order declares one of them.
 */
export function EwcCodeList({ value, defaultCode, onChange, disabled }: EwcCodeListProps) {
  const { t } = useTranslation('configuration');
  const current = defaultCode && value.some((w) => w.code === defaultCode) ? defaultCode : (value[0]?.code ?? null);

  const remove = (code: string) => {
    const next = value.filter((w) => w.code !== code);
    onChange(next, current === code ? (next[0]?.code ?? null) : current);
  };

  return (
    <div className="space-y-2">
      {value.map((w) => {
        const isDefault = w.code === current;
        return (
          <div key={w.code} className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
            <span className="rounded-sm bg-slate-200 px-1.5 py-0.5 font-mono text-[11px] font-bold text-slate-800">
              {w.code}
            </span>
            <span className="min-w-0 flex-1 font-semibold text-slate-900">{w.description}</span>
            {w.hazardous && <HazardousBadge />}
            {value.length > 1 &&
              (isDefault ? (
                <span
                  className="rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-purple-700 ring-1 ring-inset ring-purple-200"
                  title={t('products.ewc.defaultHint')}
                >
                  {t('products.ewc.default')}
                </span>
              ) : (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange(value, w.code)}
                  className="text-xs font-medium text-slate-500 hover:text-purple-600 disabled:opacity-50"
                >
                  {t('products.ewc.makeDefault')}
                </button>
              ))}
            <button
              type="button"
              disabled={disabled}
              onClick={() => remove(w.code)}
              className="text-xs font-medium text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              {t('products.ewc.remove')}
            </button>
          </div>
        );
      })}
      <EwcPicker
        value={null}
        disabled={disabled}
        label={value.length > 0 ? t('products.ewc.addLabel') : undefined}
        onChange={(picked) => {
          if (!picked || value.some((w) => w.code === picked.code)) return;
          onChange([...value, picked], current ?? picked.code);
        }}
      />
    </div>
  );
}

// ── Shared pieces ───────────────────────────────────────────────────────────

export function HazardousBadge() {
  const { t } = useTranslation('configuration');
  return (
    <span className="inline-flex shrink-0 items-center rounded-sm bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-700 ring-1 ring-inset ring-red-200">
      {t('products.hazardousBadge')}
    </span>
  );
}

function SelectionCard({
  heading,
  facts,
  footnote,
  changeLabel,
  removeLabel,
  onChange,
  onRemove,
  disabled,
}: {
  heading: ReactNode;
  facts?: [string, string][];
  footnote?: string | null;
  changeLabel: string;
  removeLabel: string;
  onChange: () => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const shown = (facts ?? []).filter(([, v]) => v && v !== '-');
  return (
    <div className="rounded-sm border border-slate-300 bg-white px-2.5 py-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs">{heading}</div>
        <div className="flex shrink-0 items-center gap-2 text-xs">
          <button
            type="button"
            onClick={onChange}
            disabled={disabled}
            className="font-medium text-purple-600 hover:underline disabled:opacity-50"
          >
            {changeLabel}
          </button>
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="font-medium text-red-600 hover:underline disabled:opacity-50"
          >
            {removeLabel}
          </button>
        </div>
      </div>
      {(shown.length > 0 || footnote) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600">
          {shown.map(([label, v]) => (
            <span key={label}>
              <span className="text-slate-400">{label}</span>{' '}
              <span className="font-semibold text-slate-800">{v}</span>
            </span>
          ))}
          {footnote && <span className="ml-auto text-slate-400">{footnote}</span>}
        </div>
      )}
    </div>
  );
}

function ResultList({
  busy,
  busyLabel,
  empty,
  emptyLabel,
  more,
  children,
}: {
  busy: boolean;
  busyLabel: string;
  empty: boolean;
  emptyLabel: string;
  more: string | null;
  children: ReactNode;
}) {
  return (
    <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-auto rounded-sm border border-slate-300 bg-white shadow-lg">
      {children}
      {busy && <p className="px-2.5 py-2 text-xs italic text-slate-500">{busyLabel}</p>}
      {!busy && empty && <p className="px-2.5 py-2 text-xs text-slate-500">{emptyLabel}</p>}
      {more && (
        <p className="border-t border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-500">
          {more}
        </p>
      )}
    </div>
  );
}

function ResultButton({ onPick, children }: { onPick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      // mousedown, not click: the input's blur would otherwise race the pick.
      onMouseDown={(e) => {
        e.preventDefault();
        onPick();
      }}
      className="flex w-full items-start gap-2 border-b border-slate-100 px-2.5 py-1.5 text-left text-xs last:border-b-0 hover:bg-purple-50"
    >
      {children}
    </button>
  );
}
